import React, { useState, useEffect, useRef } from 'react';
import '../colors.css';
import API_BASE_URL from '../api';

const ChatWindow = ({ selectedChatUserId }) => {
  const [messages, setMessages] = useState([]);
  const [chatPartner, setChatPartner] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const getAuthToken = () => localStorage.getItem('stack_access_token');
  const chatEndRef = useRef(null);

  useEffect(() => {
    const token = getAuthToken();
    if (!selectedChatUserId) return;

    console.log(`Fetching chat messages for userId: ${selectedChatUserId}`);

    fetch(`${API_BASE_URL}/api/messages/chat/${selectedChatUserId}`, {
      method: 'GET',
      headers: {
        'X-Stack-Access-Token': token,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch chat messages');
        }
        return response.json();
      })
      .then((data) => {
        setMessages(data);
        console.log('Received Chat Messages:', data);

        if (data.length > 0) {
          // Ensure chat partner's name is correctly set
          const partnerName =
            data[0].sender_id === selectedChatUserId
              ? data[0].sender_name
              : data[0].receiver_name;
          setChatPartner(partnerName);
          console.log(`👤 Chat Partner: ${partnerName}`);
        }
      })
      .catch((error) => console.error('Error fetching chat messages:', error));
  }, [selectedChatUserId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    const token = getAuthToken();
    if (!newMessage.trim()) return;

    try {
      console.log('Sending Message:', {
        receiverId: selectedChatUserId,
        text: newMessage,
      });

      const response = await fetch(`${API_BASE_URL}/api/messages/send`, {
        method: 'POST',
        headers: {
          'X-Stack-Access-Token': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          receiverId: selectedChatUserId,
          messageText: newMessage,
        }),
      });

      if (!response.ok) throw new Error('Failed to send message');

      console.log('Message Sent Successfully!');

      setMessages([
        ...messages,
        {
          sender_id: parseInt(localStorage.getItem('user_id')),
          receiver_id: selectedChatUserId,
          message_text: newMessage,
          created_at: new Date().toISOString(),
          is_read: false,
        },
      ]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-gray-800">
      {/* Chat Header */}
      <div className="p-4 bg-gray-900 text-white flex items-center border-b border-gray-700">
        <h2 className="text-xl font-semibold">{chatPartner || 'Chat'}</h2>
      </div>

      {/* Messages Container (Scrollable) */}
      <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-gray-800">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`p-3 rounded-lg max-w-xs ${
              msg.sender_id === parseInt(localStorage.getItem('user_id'))
                ? 'bg-primary text-white ml-auto' // Sender's Messages: Right-Aligned
                : 'bg-secondary text-white mr-auto' // Receiver's Messages: Left-Aligned
            }`}
            style={{
              alignSelf:
                msg.sender_id === parseInt(localStorage.getItem('user_id'))
                  ? 'flex-end'
                  : 'flex-start',
            }}
          >
            <p className="text-sm">{msg.message_text}</p>
            <p className="text-xs text-gray-300 mt-1">
              {new Date(msg.created_at).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        ))}
        {/* Auto-scroll anchor */}
        <div ref={chatEndRef} />
      </div>

      {/* Input Bar (Sticky to Bottom) */}
      <div className="p-4 bg-gray-900 flex sticky bottom-0 border-t border-gray-700">
        <input
          type="text"
          placeholder="Type a message..."
          className="flex-1 p-2 rounded-lg bg-gray-700 text-white outline-none"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSendMessage();
            }
          }}
        />
        <button
          onClick={handleSendMessage}
          className="ml-2 px-4 py-2 bg-primary text-secondary rounded-lg"
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default ChatWindow;
